import type { WorkflowRunStatus, WorkflowRunSummary } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getWorkflowRuns } from '@/config/api-helpers';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';
import { recordVisit } from '@/hooks/useVisitHistory';
import { formatTimestamp } from '@/lib/utils';
import { formatDuration, getContextPreview, StatusBadge } from '@/pages/workflows/workflowRunUi';

export const meta = {
  label: 'Job Workflows',
  order: 11,
  section: 'Jobs',
};

const VALID_DAYS = [1, 7, 14, 30] as const;
const WORKFLOW_REFRESH_INTERVAL_MS = 10_000;

const paramDefs = {
  days: {
    default: 7,
    parse: (raw: string) => {
      const parsed = Number(raw);
      return VALID_DAYS.includes(parsed as (typeof VALID_DAYS)[number]) ? parsed : 7;
    },
  },
  query: { default: '' },
  status: {
    default: 'all' as WorkflowRunStatus | 'all',
    parse: (raw: string): WorkflowRunStatus | 'all' =>
      ['running', 'succeeded', 'failed', 'halted', 'retrying', 'unknown'].includes(raw)
        ? (raw as WorkflowRunStatus)
        : 'all',
  },
  workflowId: { default: '' },
};

function WorkflowFilters({
  days,
  query,
  status,
  workflowId,
  workflowIds,
  setDays,
  setQuery,
  setStatus,
  setWorkflowId,
}: {
  days: number;
  query: string;
  status: WorkflowRunStatus | 'all';
  workflowId: string;
  workflowIds: string[];
  setDays: (days: number) => void;
  setQuery: (query: string) => void;
  setStatus: (status: WorkflowRunStatus | 'all') => void;
  setWorkflowId: (workflowId: string) => void;
}) {
  return (
    <div className='mb-4 grid gap-3 border-b border-neutral-100 pb-4 lg:grid-cols-[1fr_auto_auto_auto]'>
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder='Search run, step, providerId, applicationId'
        className='h-9 rounded border border-neutral-200 px-3 text-sm outline-none focus:border-neutral-500'
      />
      <select
        value={workflowId}
        onChange={(event) => setWorkflowId(event.target.value)}
        className='h-9 rounded border border-neutral-200 bg-white px-3 text-sm text-neutral-700'
      >
        <option value=''>All workflows</option>
        {workflowIds.map((id) => (
          <option key={id} value={id}>
            {id}
          </option>
        ))}
      </select>
      <select
        value={status}
        onChange={(event) => setStatus(event.target.value as WorkflowRunStatus | 'all')}
        className='h-9 rounded border border-neutral-200 bg-white px-3 text-sm text-neutral-700'
      >
        <option value='all'>All statuses</option>
        <option value='running'>Running</option>
        <option value='retrying'>Retrying</option>
        <option value='failed'>Failed</option>
        <option value='halted'>Halted</option>
        <option value='succeeded'>Succeeded</option>
        <option value='unknown'>Unknown</option>
      </select>
      <div className='flex items-center gap-1'>
        <span className='mr-1 text-sm text-neutral-500'>Started in last</span>
        {VALID_DAYS.map((dayCount) => (
          <button
            key={dayCount}
            onClick={() => setDays(dayCount)}
            className={`h-9 rounded px-3 text-sm ${
              days === dayCount
                ? 'bg-neutral-900 text-white'
                : 'border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400'
            }`}
          >
            {dayCount}d
          </button>
        ))}
      </div>
    </div>
  );
}

function DefinitionsPanel({
  definitions,
}: {
  definitions: NonNullable<Awaited<ReturnType<typeof getWorkflowRuns>>>['definitions'];
}) {
  if (definitions.every((definition) => definition.runCount === 0)) {
    return <EmptyState message='No workflow lifecycle events found for this range.' />;
  }

  return (
    <div className='rounded border border-neutral-200 bg-white'>
      <div className='font-mediumbold border-b border-neutral-100 px-3 py-2 text-sm text-neutral-900'>Workflows</div>
      <div className='divide-y divide-neutral-100'>
        {definitions.map((definition) => (
          <div key={definition.workflowId} className='p-3'>
            <div className='flex items-center justify-between gap-3'>
              <div className='font-mediumbold truncate text-sm text-neutral-900'>{definition.workflowId}</div>
              <div className='text-xs text-neutral-500'>{definition.runCount} runs</div>
            </div>
            <div className='mt-1 text-xs text-neutral-500'>
              {definition.lastEventAt == null
                ? 'No runs in this range'
                : `${definition.failedRunCount} failed · last ${formatTimestamp(definition.lastEventAt)}`}
            </div>
            <div className='mt-2 flex flex-wrap gap-1'>
              {definition.observedStepKeys.length === 0 ? (
                <span className='text-xs text-neutral-400'>No observed steps</span>
              ) : (
                definition.observedStepKeys.map((stepKey) => (
                  <span key={stepKey} className='rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-700'>
                    {stepKey}
                  </span>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RunsTable({ runs }: { runs: WorkflowRunSummary[] }) {
  const navigate = useNavigate();

  if (runs.length === 0) {
    return <EmptyState message='No workflow runs match these filters.' />;
  }

  const openRun = (runId: string) => navigate(`/workflows/${encodeURIComponent(runId)}`);

  return (
    <div className='overflow-hidden rounded border border-neutral-200 bg-white'>
      <table className='w-full table-fixed text-left text-sm'>
        <thead className='border-b border-neutral-100 bg-neutral-50 text-xs uppercase text-neutral-500'>
          <tr>
            <th className='w-[28%] px-3 py-2 font-medium'>Run</th>
            <th className='w-[16%] px-3 py-2 font-medium'>Status</th>
            <th className='w-[18%] px-3 py-2 font-medium'>Step</th>
            <th className='w-[16%] px-3 py-2 font-medium'>Last Event</th>
            <th className='w-[12%] px-3 py-2 font-medium'>Duration</th>
            <th className='px-3 py-2 font-medium'>Context</th>
          </tr>
        </thead>
        <tbody className='divide-y divide-neutral-100'>
          {runs.map((run) => (
            <tr
              key={run.runId}
              role='button'
              tabIndex={0}
              onClick={() => openRun(run.runId)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openRun(run.runId);
                }
              }}
              className='cursor-pointer bg-white hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none'
            >
              <td className='p-3 align-top'>
                <div className='font-mediumbold truncate text-neutral-900'>{run.workflowId}</div>
                <div className='truncate text-xs text-neutral-500'>{run.runId}</div>
              </td>
              <td className='p-3 align-top'>
                <StatusBadge status={run.status} />
              </td>
              <td className='truncate p-3 align-top text-neutral-700'>{run.currentStepKey ?? '-'}</td>
              <td className='p-3 align-top text-xs text-neutral-600'>{formatTimestamp(run.lastEventAt)}</td>
              <td className='p-3 align-top text-neutral-700'>{formatDuration(run.durationMs)}</td>
              <td className='truncate p-3 align-top text-xs text-neutral-500'>
                {getContextPreview(run.logContext, 3)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function WorkflowsPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const {
    days: [days, setDays],
    query: [query, setQuery],
    status: [status, setStatus],
    workflowId: [workflowId, setWorkflowId],
  } = useSearchParamsState(paramDefs);

  useEffect(() => {
    recordVisit('/workflows', 'Workflows', 'workflow');
  }, []);

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['workflow-runs', days, query, workflowId],
    queryFn: () => getWorkflowRuns({ days, query, workflowId }),
    refetchInterval: autoRefresh ? WORKFLOW_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  const filteredRuns = useMemo(() => {
    const runs = data?.runs ?? [];
    return status === 'all' ? runs : runs.filter((run) => run.status === status);
  }, [data?.runs, status]);

  const workflowIds = useMemo(() => data?.definitions.map((definition) => definition.workflowId) ?? [], [data]);

  return (
    <div>
      <div className='mb-4 flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='font-mediumbold text-xl text-neutral-900'>Workflows</h2>
          <p className='mt-1 text-sm text-neutral-500'>
            Runs from the workflow-runs table
            {data != null ? ` · ${data.eventsScanned} events read` : ''}
          </p>
          {data != null && data.unreadRunCount > 0 ? (
            <p className='mt-1 text-sm text-amber-700'>
              {data.unreadRunCount} older runs are listed without their events, so search cannot match their metadata.
              Narrow by workflow or days to include them.
            </p>
          ) : null}
        </div>
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <label className='flex h-9 items-center gap-2 rounded border border-neutral-200 bg-white px-3 text-sm text-neutral-600'>
            <input checked={autoRefresh} onChange={(event) => setAutoRefresh(event.target.checked)} type='checkbox' />
            Auto-refresh (10s)
          </label>
          <button
            onClick={() => refetch()}
            className='h-9 rounded border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400'
          >
            Refresh
          </button>
        </div>
      </div>

      <WorkflowFilters
        days={days}
        query={query}
        status={status}
        workflowId={workflowId}
        workflowIds={workflowIds}
        setDays={setDays}
        setQuery={setQuery}
        setStatus={setStatus}
        setWorkflowId={setWorkflowId}
      />

      {error ? (
        <ErrorBanner
          message={error instanceof Error ? error.message : 'Failed to load workflow runs'}
          onRetry={refetch}
        />
      ) : isLoading ? (
        <LoadingCard message='Loading workflow runs…' />
      ) : (
        <div className='space-y-4'>
          <DefinitionsPanel definitions={data?.definitions ?? []} />
          <RunsTable runs={filteredRuns} />
        </div>
      )}
    </div>
  );
}
